/**
 * lib/currency-cookie.ts — the shop currency CHOICE, as a cookie.
 *
 * Spec §9 + brief 2026-09-14: the currency toggle changes currency only (never
 * the tax band) and the choice persists in a cookie so it carries into the
 * checkout page, which reads it SERVER-SIDE.
 *
 * 2026-10-05 (LANE-shop-currency-default) — the choice moved to a NEW cookie,
 * `pf_currency_choice`. The old `pf_currency` cookie carried no proof of where
 * it came from: a USD value written by any earlier click (or at a narrower
 * path than `/`) kept a visitor in the Netherlands on dollars for a year, on
 * some shop pages and not others. Nothing reads `pf_currency` any more, and
 * every shop page clears it. So:
 *
 *  1. `pf_currency_choice` is written ONLY by writeCurrencyCookie(), and
 *     writeCurrencyCookie() is called ONLY from a toggle click handler —
 *     the page toggles on /super-fingers, /store/distance, /pro-member, /join
 *     and the CheckoutModal toggle. Never on load.
 *  2. The server reads ONLY `pf_currency_choice` (lib/shop-currency.ts,
 *     /checkout/membership); without it, the IP country decides.
 *  3. The retired `pf_currency` cookie (any path) and the retired
 *     `pf-currency` localStorage key are IGNORED and CLEARED on shop page
 *     loads (clearLegacyCurrency).
 */
export type ShopCurrency = 'EUR' | 'USD';

/** The ONE cookie a currency choice lives in. Written only by a toggle click. */
export const CURRENCY_CHOICE_COOKIE = 'pf_currency_choice';
/** Retired 2026-10-05 — never read; cleared by clearLegacyCurrency(). */
const LEGACY_CURRENCY_COOKIE = 'pf_currency';
/** Retired 2026-09-14 — never read; cleared by clearLegacyCurrency(). */
const LEGACY_LOCALSTORAGE_KEY = 'pf-currency';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

export function parseCurrency(value: string | null | undefined): ShopCurrency | null {
  return value === 'EUR' || value === 'USD' ? value : null;
}

function secureAttr(): string {
  return window.location.protocol === 'https:' ? '; Secure' : '';
}

/** Client: read the choice cookie (null when the visitor never clicked a toggle). */
export function readCurrencyCookie(): ShopCurrency | null {
  if (typeof document === 'undefined') return null;
  const hit = document.cookie.split('; ').find((c) => c.startsWith(`${CURRENCY_CHOICE_COOKIE}=`));
  return parseCurrency(hit?.slice(CURRENCY_CHOICE_COOKIE.length + 1));
}

/** Client: call ONLY from a toggle click handler. */
export function writeCurrencyCookie(currency: ShopCurrency): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${CURRENCY_CHOICE_COOKIE}=${currency}; Path=/; Max-Age=${ONE_YEAR_SECONDS}; SameSite=Lax${secureAttr()}`;
  clearLegacyCurrency();
}

/**
 * Client: drop the retired `pf_currency` cookie and `pf-currency` localStorage
 * key. Safe to call on every load — it never touches `pf_currency_choice`.
 *
 * A cookie can only be deleted with the path (and domain) it was set with, and
 * the browser does not say which that was, so this expires `pf_currency` at `/`
 * and at every path on the way to the current page (/store, /store/distance …),
 * host-only and on the parent domain.
 */
export function clearLegacyCurrency(): void {
  if (typeof document === 'undefined') return;
  try {
    window.localStorage.removeItem(LEGACY_LOCALSTORAGE_KEY);
  } catch {
    /* private browsing — nothing stored anyway */
  }
  const paths = ['/'];
  let acc = '';
  for (const part of window.location.pathname.split('/').filter(Boolean)) {
    acc += `/${part}`;
    paths.push(acc, `${acc}/`);
  }
  const host = window.location.hostname;
  const domains = [''];
  if (host === 'pianofantasy.com' || host.endsWith('.pianofantasy.com')) {
    domains.push('; Domain=pianofantasy.com');
  }
  for (const path of paths) {
    for (const domain of domains) {
      document.cookie = `${LEGACY_CURRENCY_COOKIE}=; Path=${path}${domain}; Max-Age=0; SameSite=Lax${secureAttr()}`;
    }
  }
}
